import { useTranslation } from 'react-i18next';
import { ChangeEvent, FormEvent, useEffect, useRef } from 'react';
import { DashValidation, isEmptyOrNil, isNameLengthValid } from 'dash-common';
import {
  Form,
  FormErrorTypes,
  Flex,
  toast,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
  Button,
  useScreenResolution,
} from 'dash-ui';

import {
  GetPortfoliosTreeDocument,
  useEditPortfolioFolderNameMutation,
} from 'gql/generated.graphql';
import ChangesConfirmationModal from 'containers/portfolio/ChangesConfirmationModal/ChangesConfirmationModal';

enum FormFields {
  FolderName = 'folderName',
}

const EditPortfolioNameForm = ({
  onClose,
  value,
  portfolioId,
}: {
  onClose: () => void;
  value: string;
  portfolioId: string;
}) => {
  const ref = useRef<HTMLFormElement | null>(null);
  const {
    devices: { isMobile },
  } = useScreenResolution();
  const [formState, [setFolderName]] = useFormState([FormFields.FolderName]);
  const [formErrors, , [setFolderError]] = useFormValidation([
    FormFields.FolderName,
  ]);
  const { t } = useTranslation(['pages', 'common']);
  const translateErrorMessage = useServerResponseTranslation();
  const [editFolder, { loading }] = useEditPortfolioFolderNameMutation();

  // set default value effect
  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    setFolderName(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleFolderName = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setFolderName(value);
    if (isEmptyOrNil(value)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.nameBlank'),
      });
    }
    if (!isNameLengthValid(value)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
    }

    if (value.includes("/")) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.invalidCharacters"),
      });
    }
    setFolderError(null);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();

    if (isEmptyOrNil(formState.folderName)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.nameBlank'),
      });
    }
    if (!isNameLengthValid(formState.folderName)) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
    }

    if(formState.folderName.includes("/")) {
      return setFolderError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.invalidCharacters"),
      });
    }

    editFolder({
      variables: {
        name: formState.folderName,
        portfolioFolderId: portfolioId,
      },
      refetchQueries: [
        {
          query: GetPortfoliosTreeDocument,
          fetchPolicy: 'network-only',
        },
      ],
      onCompleted: () => {
        toast.success(t('pages:portfoliosPage.toasts.folderIsUpdated'));
        onClose();
      },
      onError: (error) => toast.error(translateErrorMessage(error)),
    });
  };

  return (
    <>
      <Form
        w="100%"
        flexDirection={!isMobile ? 'row' : 'column'}
        isFlex
        ref={ref}
        onSubmit={onSubmit}
      >
        <Flex w="100%" flexDirection="column" mr={2}>
          <Form.Label>{t('pages:portfoliosPage.portfolioName')}</Form.Label>
          <Form.Input
            w="100%"
            value={formState.folderName}
            placeholder={
              t('pages:portfoliosPage.portfolioNamePlaceholder') || ''
            }
            onChange={handleFolderName}
            $errorType={formErrors[FormFields.FolderName]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.FolderName]?.type}>
            {formErrors[FormFields.FolderName]?.message}
          </Form.Feedback>
        </Flex>
        <Flex mt={isMobile ? 2 : 4}>
          <Button
            isLoading={loading}
            disabled={loading}
            px={isMobile ? 2 : 4}
            medium={isMobile}
            icon="check"
            variation="primary"
            colorVariation="success"
            mr={2}
          />
          <Button
            type="button"
            px={isMobile ? 2 : 4}
            medium={isMobile}
            icon="close"
            variation="outline"
            colorVariation="danger"
            onClick={onClose}
          />
        </Flex>
      </Form>
      <ChangesConfirmationModal condition={value !== formState.folderName} />
    </>
  );
};

export default EditPortfolioNameForm;
